import { useRef, useEffect } from 'react';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';

import { useModelPropertiesStore } from '../../store/modelPropertiesStore/modelPropertiesStore';

/**
 * Debug component that renders wireframe boxes for both bounding boxes in modelPropertiesStore:
 *  - boundingBox        (red)   — current world-space AABB, inflated by model rotation
 *  - alignedBoundingBox (green) — tight OBB in world space, correctly oriented with the model
 */
function BoundingBoxVisualizer() {
    const { scene } = useThree();
    const { boundingBox, alignedBoundingBox } = useModelPropertiesStore();

    const aabbHelperRef = useRef<THREE.Box3Helper | null>(null);
    const obbLinesRef = useRef<THREE.LineSegments | null>(null);

    // World AABB (red Box3Helper)
    useEffect(() => {
        if (aabbHelperRef.current) {
            scene.remove(aabbHelperRef.current);
            aabbHelperRef.current.dispose();
            aabbHelperRef.current = null;
        }

        if (boundingBox && !boundingBox.isEmpty()) {
            const helper = new THREE.Box3Helper(boundingBox.clone(), new THREE.Color(0xff2200));
            helper.name = 'debug_bounding_box';
            scene.add(helper);
            aabbHelperRef.current = helper;
        }

        return () => {
            if (aabbHelperRef.current) {
                scene.remove(aabbHelperRef.current);
                aabbHelperRef.current.dispose();
                aabbHelperRef.current = null;
            }
        };
    }, [boundingBox, scene]);

    // Aligned OBB (green wireframe, orientation taken directly from OBB.rotation)
    useEffect(() => {
        if (obbLinesRef.current) {
            scene.remove(obbLinesRef.current);
            obbLinesRef.current = null;
        }

        if (alignedBoundingBox && alignedBoundingBox.halfSize.lengthSq() > 0) {
            const { center, halfSize, rotation } = alignedBoundingBox;
            const size = halfSize.clone().multiplyScalar(2);

            const edges = new THREE.EdgesGeometry(new THREE.BoxGeometry(size.x, size.y, size.z));
            const material = new THREE.LineBasicMaterial({ color: 0x00ff44 });
            const lines = new THREE.LineSegments(edges, material);

            lines.position.copy(center);
            // col0 of the OBB rotation matrix = local X axis in world: (cos, sin, 0)
            lines.rotation.z = Math.atan2(rotation.elements[1], rotation.elements[0]);
            lines.name = 'debug_aligned_bounding_box';

            scene.add(lines);
            obbLinesRef.current = lines;
        }

        return () => {
            if (obbLinesRef.current) {
                scene.remove(obbLinesRef.current);
                obbLinesRef.current = null;
            }
        };
    }, [alignedBoundingBox, scene]);

    return null;
}

export default BoundingBoxVisualizer;

